/** What a document can be shown as. `unsupported` is a real answer, not a
 *  failure: a `.docx` cannot be rendered by any browser, and pretending
 *  otherwise costs the reader a blank frame before they find that out. */
export type DocumentKind = 'pdf' | 'image' | 'unsupported';

/** Extensions the image viewer can take. The feed's non-PDF documents are
 *  overwhelmingly scans and photos of signed paper, which is why they are worth
 *  rendering rather than sending straight to a download. */
const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif'];

/**
 * The extension a URL ends in, ignoring everything a signed URL adds.
 *
 * A presigned S3 URL carries its signature in the query string, so the naive
 * "text after the last dot" is `com%2F20260901%2Fap-southeast-2` rather than
 * `pdf`. The query and fragment are cut before the path is read for that
 * reason.
 */
const extensionOf = (url: string): string => {
  const path = url.split(/[?#]/)[0] ?? '';
  const segment = path.split('/').pop() ?? '';
  const dot = segment.lastIndexOf('.');

  if (dot === -1) return '';

  // An upload named with a space arrives percent-encoded; a malformed encoding
  // is not worth throwing over, so the raw extension stands.
  const raw = segment.slice(dot + 1).toLowerCase();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
};

/**
 * How a document should be shown, from its URL and — when the server said so —
 * its MIME type.
 *
 * The content type is preferred where it is meaningful, because it is what the
 * server actually committed to serving. It is often not meaningful: the API
 * signs a good many objects as `application/octet-stream`, which says only
 * "bytes" and must not be allowed to outrank a filename that says `.pdf`.
 *
 * The fallback for an unrecognised file is `pdf`, not `unsupported`. Nearly
 * every document in this portal is a PDF; a missing extension is far more
 * likely to be an object key without one than a genuinely unviewable file, and
 * the viewer's own error state already covers the case where the guess is
 * wrong. Refusing to try would fail the common case to be right about the rare
 * one.
 */
export const documentKindOf = (url: string, contentType?: string): DocumentKind => {
  const type = (contentType ?? '').toLowerCase();

  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf') return 'pdf';

  const extension = extensionOf(url);

  if (IMAGE_EXTENSIONS.includes(extension)) return 'image';
  if (extension === 'pdf') return 'pdf';
  // Office documents and Outlook messages: real entries in this feed, and
  // nothing a browser will render.
  if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'msg', 'eml', 'zip'].includes(extension)) {
    return 'unsupported';
  }

  return 'pdf';
};
